import { IGenericJSON } from '../helpers/interface';

export type OnlinePlatform = 'Google meet' | 'Microsoft Teams' | 'Zoom';

export interface TimeInterval {
  start: Date | null;
  end: Date | null;
}

/**
 * A day of the week and the intervals the user is available on it.
 */
export interface DayAvailability {
  day: string;
  dayOfWeek: number;
  enabled: boolean;
  intervals: TimeInterval[];
}

/**
 * A one-off override of the recurring weekly template, for a single day, a
 * time window within a single day, or a range of days.
 */
export interface BlockedPeriod {
  id: number;
  startDate: Date;
  endDate: Date;
  allDay: boolean;
  startTime: Date | null;
  endTime: Date | null;
  reason: string;
}

export const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

export class MeetingPreferences {
  weeklyAvailability: DayAvailability[];
  blockedAvailabilityIntervals: BlockedPeriod[];
  allowedDurations: number[];
  meetingsPerDay: number | null;
  instructions: string | null;
  // This is a read only value which may be used on the
  // student side to evaluate next available meeting.
  bufferMinutes: number | null;
  inPersonLocation: string | null;
  onlinePlatform: OnlinePlatform | null;

  constructor(data: IGenericJSON = {}) {
    this.weeklyAvailability = MeetingPreferences.groupIntervals(
      data['availabilityIntervals'] ?? data['availability_intervals'] ?? [],
    );
    this.blockedAvailabilityIntervals = MeetingPreferences.digestBlocks(
      data['blockedAvailabilityIntervals'] ?? [],
    );
    this.allowedDurations = data['allowedDurations'] ?? [];
    this.meetingsPerDay = data['meetingsPerDay'] ?? null;
    this.instructions = data['instructions'] ?? null;
    this.bufferMinutes = data['bufferMinutes'] ?? null;
    this.inPersonLocation = data['inPersonLocation'] ?? null;
    this.onlinePlatform = data['onlinePlatform'] ?? null;
  }

  /**
   * The upsert payload. Availability flattens back to one row per interval;
   * incomplete intervals and disabled days are simply omitted, which is how
   * "unavailable" is represented server-side.
   */
  toPayload(): IGenericJSON {
    return {
      allowedDurations: this.allowedDurations,
      meetingsPerDay: this.meetingsPerDay,
      instructions: this.instructions,
      inPersonLocation: this.inPersonLocation,
      onlinePlatform: this.onlinePlatform,
      availabilityIntervals: this.weeklyAvailability.flatMap((day) =>
        day.enabled
          ? day.intervals
              .filter((interval) => interval.start && interval.end)
              .map((interval) => ({
                day_of_week: day.day,
                start_time: MeetingPreferences.toTimeString(interval.start!),
                end_time: MeetingPreferences.toTimeString(interval.end!),
              }))
          : [],
      ),
      blockedAvailabilityIntervals: this.blockedAvailabilityIntervals.map((block) => ({
        start_date: MeetingPreferences.toDateString(block.startDate),
        end_date: MeetingPreferences.toDateString(block.endDate),
        all_day: block.allDay,
        start_time: block.startTime ? MeetingPreferences.toTimeString(block.startTime) : null,
        end_time: block.endTime ? MeetingPreferences.toTimeString(block.endTime) : null,
        reason: block.reason || null,
      })),
    };
  }

  /** Builds a model from a single fetch response, matching the other models. */
  public static digest(data: IGenericJSON): MeetingPreferences {
    return new MeetingPreferences(data);
  }

  /** Independent copy, used for the cancel snapshot. */
  clone(): MeetingPreferences {
    const copy = new MeetingPreferences();
    copy.weeklyAvailability = MeetingPreferences.cloneDays(this.weeklyAvailability);
    copy.blockedAvailabilityIntervals = MeetingPreferences.cloneBlocks(
      this.blockedAvailabilityIntervals,
    );
    copy.allowedDurations = [...this.allowedDurations];
    copy.meetingsPerDay = this.meetingsPerDay;
    copy.instructions = this.instructions;
    copy.bufferMinutes = this.bufferMinutes;
    copy.inPersonLocation = this.inPersonLocation;
    copy.onlinePlatform = this.onlinePlatform;
    return copy;
  }

  /** Returns a list of default, zero interval days where meetings are disabled. */
  static defaultDays(): DayAvailability[] {
    return WEEKDAYS.map((day, dayOfWeek) => ({
      day,
      dayOfWeek,
      enabled: false,
      intervals: [],
    }));
  }

  /** The standard 9–3 window used when a day is first enabled. */
  static defaultInterval(): TimeInterval {
    return {
      start: MeetingPreferences.at(9, 0),
      end: MeetingPreferences.at(15, 0),
    };
  }

  /**
   * The interval to append after `previous`, starting where it ended so a newly
   * added row doesn't overlap by default.
   */
  static nextInterval(previous: TimeInterval | undefined): TimeInterval {
    if (!previous?.end) return MeetingPreferences.defaultInterval();
    const start = new Date(previous.end);
    const end = new Date(previous.end);
    end.setHours(end.getHours() + 1);
    // Guard against rolling past midnight into the next day.
    if (end.getDate() !== start.getDate()) {
      end.setTime(start.getTime());
      end.setHours(23, 59, 0, 0);
    }
    return { start, end };
  }

  /** Deep-clone so a snapshot is independent of the live draft. */
  static cloneDays(days: DayAvailability[]): DayAvailability[] {
    return days.map((day) => ({
      ...day,
      intervals: day.intervals.map((interval) => ({
        start: interval.start ? new Date(interval.start) : null,
        end: interval.end ? new Date(interval.end) : null,
      })),
    }));
  }

  /** Deep-clone so a snapshot is independent of the live draft. */
  static cloneBlocks(blocks: BlockedPeriod[]): BlockedPeriod[] {
    return blocks.map((block) => ({
      ...block,
      startDate: new Date(block.startDate),
      endDate: new Date(block.endDate),
      startTime: block.startTime ? new Date(block.startTime) : null,
      endTime: block.endTime ? new Date(block.endTime) : null,
    }));
  }

  /** Flat interval rows → one entry per weekday, in calendar order. */
  private static groupIntervals(rows: IGenericJSON[]): DayAvailability[] {
    const days = MeetingPreferences.defaultDays();

    for (const row of rows) {
      const name = row['day_of_week'] ?? row['dayOfWeek'];
      const day = days.find((d) => d.day === name);
      if (!day) continue;

      day.intervals.push({
        start: MeetingPreferences.fromTimeString(row['start_time'] ?? row['startTime']),
        end: MeetingPreferences.fromTimeString(row['end_time'] ?? row['endTime']),
      });
      day.enabled = true;
    }

    return days;
  }

  /** Raw blocked-period rows → BlockedPeriod objects with parsed dates/times. */
  private static digestBlocks(rows: IGenericJSON[]): BlockedPeriod[] {
    return rows.map((row) => ({
      id: row['id'],
      startDate: MeetingPreferences.fromDateString(row['start_date'] ?? row['startDate']),
      endDate: MeetingPreferences.fromDateString(row['end_date'] ?? row['endDate']),
      allDay: row['all_day'] ?? row['allDay'],
      startTime: MeetingPreferences.fromTimeString(row['start_time'] ?? row['startTime']),
      endTime: MeetingPreferences.fromTimeString(row['end_time'] ?? row['endTime']),
      reason: row['reason'] ?? '',
    }));
  }

  // ── Time conversion — mat-timepicker works in Date objects, but TIME
  // columns are 'HH:MM:SS' strings, so these convert between the two. ──

  /** A TIME column value ("09:30:00") as a Date on today's calendar day. */
  private static fromTimeString(raw: string | null | undefined): Date | null {
    if (!raw) return null;
    const [hours, minutes] = raw.split(':').map(Number);
    return MeetingPreferences.at(hours || 0, minutes || 0);
  }

  /** A Date as a TIME column value; only the clock time is significant. */
  private static toTimeString(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
  }

  // ── Date conversion — mat-datepicker works in Date objects, but DATEONLY
  // columns are 'YYYY-MM-DD' strings, so these convert between the two. ──

  /** A DATEONLY column value ("2026-08-15") as a Date at local midnight. */
  private static fromDateString(raw: string): Date {
    const [year, month, day] = raw.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  /** A Date as a DATEONLY column value; only the calendar date is significant. */
  private static toDateString(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  /** Today's date at a given clock time — the date part is never persisted. */
  static at(hours: number, minutes: number): Date {
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  }
}
